// The user document holds memberships as { [orgId]: { id, name }, primaryOrg: "<orgId>" }. Reading
// that shape was copied into every feature that needed an organization, and each copy took only the
// primary one, which is why a second organization's library or folder was unreachable.
export const getMemberOrgIds = (organization = {}) => {
    const { primaryOrg = "", ...memberOrgs } = organization?.memberOrgIds || {};

    const named = Object.keys(memberOrgs).filter((id) => typeof memberOrgs[id] === "object" && id !== primaryOrg);

    // primary first, then alphabetical: the order organizations are listed in everywhere
    return [
        ...new Set(
            [
                primaryOrg,
                ...named.sort((a, b) => (memberOrgs[a]?.name || "").localeCompare(memberOrgs[b]?.name || "")),
            ].filter(Boolean)
        ),
    ];
};

export const getPrimaryOrgId = (organization = {}) => getMemberOrgIds(organization)[0] || "";

export const getOrgName = (organization = {}, orgId) =>
    organization?.memberOrgIds?.[orgId]?.name || organization?.details?.[orgId]?.name || "Organization";

//A folder's chain always ends at a root folder, so this is what a model's top level folder is.
const getRootFolderId = (folders = {}, folderId = null) => {
    let current = folderId;

    //a folder that points at itself, or a parent that is gone, must not spin here
    for (let i = 0; current && folders[current] && i < 100; i++) {
        const { parentFolderId = null } = folders[current] || {};

        if (!parentFolderId) return current;

        current = parentFolderId;
    }

    return current || "";
};

// Which organization a model belongs to: the one whose shared folder holds it, then the one whose
// folder it sits in inside the user's own directory, and otherwise the member's own primary
// organization. Nothing marks a model with an organization, so the trees are what the answer is
// read from.
export const getModelOrgId = (organization = {}, modelId = "", userModelDir = {}) => {
    const orgIds = getMemberOrgIds(organization);

    const sharedFolderOrgId =
        modelId && orgIds.find((orgId) => organization?.modelDir?.[orgId]?.models?.[modelId] !== undefined);

    if (sharedFolderOrgId) return sharedFolderOrgId;

    // Sharing used to copy a model into each member's own directory, under a folder carrying the
    // organization's own id. Those models belong to the organization just as much.
    const { folders = {}, models = {} } = userModelDir || {};
    const { parentFolderId = null } = models?.[modelId] || {};
    const rootFolderId = getRootFolderId(folders, parentFolderId);

    if (rootFolderId && orgIds.includes(rootFolderId)) return rootFolderId;

    return getPrimaryOrgId(organization);
};
